import { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { uniq } from 'lodash';
import { read, utils } from 'xlsx';
import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { DATE_FORMATS } from '@callmart/types';
import { uploadForecast } from '../services/forecastDelta';
import UserContext from '../store/User/UserContext';
import Container from '../components/Layout/Container';
import {
    useAlertMessage,
    useIsMounted,
    useCookieWorktype,
    useWorktypes,
    useLastForecastDeltaUploadDate,
    useAllSlotDaysInMonthAndYear,
    useAllAvailableBookingsByDateAndWorktype,
    useCustomStyles,
} from '../hooks';
import { getRowsFromXLSXData } from '../utils/xlsxUtils';
import PercentageLoadingOverlay from '../components/Loaders/PercentageLoadingOverlay';
import CustomDatePicker from '../components/UI/CustomDatePicker';
import { createTableRows, getSlotTimeRowKey, getSlotTimeColumnDef } from '../utils/agGridUtils';
import { integerComparator } from '../components/AgGrid/comparators';
import WorktypeListDialog from '../components/CalldeltaOverview/WorktypeListDialog';
import WorktypeSelect from '../components/Selects/WorktypeSelect';
import UploadXlsxFileButton from '../components/Buttons/UploadXlsxFileButton';
import { appTimezone } from '../config';
import {
    setCommonStateAfterDataUpload,
    showSuccessfulUploadNotification,
    showErrorUploadNotification,
    handleDatePickerMonthChange,
    parseUploadResponse,
} from '../utils/componentUtils';
import LastUpload from '../components/General/LastUpload';
import { queryClient } from '../services/queryClient';
import { QueryKey } from '../enums/QueryKey';
import { getShortDayNameFromDate } from '../utils/dateUtils';
import TableGridItem from '../components/UI/TableGridItem';
import GridContainer from '../components/Layout/GridContainer';
import { useSocket } from '../store/SocketContext';
import { SocketEvents } from '../enums/SocketEvents';
import dayjs, { formatSlotDay, formatSlotTime } from '@utils/dayjs';

const REQUIRED_HEADERS = ['Datum', 'Intervall', 'fc_grp'];

const findCalldeltaHeaderRow = (rows, maxScan = 5) => {
    for (let i = 0; i < Math.min(rows.length, maxScan); i++) {
        const cells = (rows[i] || []).map((c) => (c == null ? '' : String(c)));
        if (REQUIRED_HEADERS.every((h) => cells.includes(h))) return i;
    }
    return -1;
};

const createMapWithTableRows = (availableBookingsList) => {
    let rowsMap = new Map();
    rowsMap = addRowsInMap(availableBookingsList, rowsMap);

    return rowsMap;
};

const addRowsInMap = (list, rowsMap) => {
    for (let i = 0; i < list.length; i++) {
        const key = getSlotTimeRowKey(list[i].slotDate);
        const day = formatSlotDay(list[i].slotDate);

        if (rowsMap.has(key)) {
            rowsMap.set(key, {
                ...rowsMap.get(key),
                [`day${day}`]: list[i].availableBookings,
            });
        } else {
            rowsMap.set(key, {
                [`day${day}`]: list[i].availableBookings,
            });
        }
    }

    return rowsMap;
};

const createColumnDefs = (slotDaysInMonth, locale) => {
    const columns = [getSlotTimeColumnDef()];
    for (let i = 0; i < slotDaysInMonth.length; i++) {
        const dayName = getShortDayNameFromDate(slotDaysInMonth[i].date, locale);
        columns.push({
            field: `day${formatSlotDay(slotDaysInMonth[i].date)}`,
            headerName: `${formatSlotTime(slotDaysInMonth[i].date, 'DD.MM')}\n${dayName}`,
            comparator: (valueA, valueB) => {
                return integerComparator({ valueA, valueB });
            },
            cellClass: 'grid-cell-centered',
            cellStyle: (params) => {
                let styles = {
                    padding: '0px',
                    textAlign: 'center',
                };
                if (params.value > 0) {
                    styles = { ...styles, color: 'red' };
                }
                if (i % 2 === 0) {
                    styles = { ...styles, backgroundColor: 'var(--lightGrey)' };
                }
                return styles;
            },
            minWidth: 90,
        });
    }

    return columns;
};

const CalldeltaOverview = () => {
    const { t, i18n } = useTranslation('allBookings');
    const socket = useSocket();
    const classes = useCustomStyles();

    const isMounted = useIsMounted();
    const { dispatchAlert } = useAlertMessage();
    const loggedInUser = useContext(UserContext);
    // TEF aliases to WFM in HEMTService.
    const canWrite = Boolean(loggedInUser?.isWFMUser);

    const gridRef = useRef(null);

    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [worktypeOptions] = useWorktypes({ prependAllWorktypesOption: true });
    const [selectedWorktype, setSelectedWorktype] = useCookieWorktype(worktypeOptions);
    const lastUploadDate = useLastForecastDeltaUploadDate(selectedWorktype);
    const [loading, setLoading] = useState(false);
    const [percentage, setPercentage] = useState(0);
    const [monthForUploading, setMonthForUploading] = useState(dayjs().format(DATE_FORMATS.YEAR_MONTH));

    const [openWorktypeListDialog, setOpenWorktypeListDialog] = useState(false);
    const [notPresentWorktypes, setNotPresentWorktypes] = useState([]);

    const { mutateAsync: handleUploadForecast } = useMutation({
        mutationFn: uploadForecast,
    });

    const handleWorktypeListDialogOpen = useCallback(() => {
        setOpenWorktypeListDialog(true);
    }, [setOpenWorktypeListDialog]);

    const handleWorktypeListDialogClose = useCallback(() => {
        setOpenWorktypeListDialog(false);
    }, [setOpenWorktypeListDialog]);

    const { data: slotDaysInMonthAndYear, loading: isSlotsLoading } = useAllSlotDaysInMonthAndYear(selectedDate);
    const columnDefs = useMemo(() => {
        return slotDaysInMonthAndYear?.length > 0 ? createColumnDefs(slotDaysInMonthAndYear, i18n.language) : [];
    }, [slotDaysInMonthAndYear, i18n.language]);
    const { data: availableBookings, loading: isBookingsLoading } = useAllAvailableBookingsByDateAndWorktype({
        dateFrom: dayjs(selectedDate).tz(appTimezone).startOf('month'),
        dateTo: dayjs(selectedDate).tz(appTimezone).endOf('month'),
        worktype: selectedWorktype,
    });
    const tableRows = useMemo(() => {
        return availableBookings?.length > 0
            ? createTableRows(createMapWithTableRows(availableBookings), 'slotTime').sort((a, b) =>
                  (a.slotTime || '').localeCompare(b.slotTime || '')
              )
            : [];
    }, [availableBookings]);

    const uploadFiles = useCallback(
        (files, closeDropzoneDialog) => {
            setLoading(true);

            const file = files[0];

            const reader = new FileReader();
            reader.onload = async (e) => {
                /* Parse data */
                const ab = e.target.result;
                const wb = read(ab, { type: 'array' });
                /* Get first worksheet */
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                /* Convert array of arrays */
                const data = utils.sheet_to_json(ws, { header: 1 });

                const headerLine = findCalldeltaHeaderRow(data);
                if (headerLine === -1) {
                    setLoading(false);
                    closeDropzoneDialog();
                    showErrorUploadNotification({
                        dispatchAlert,
                        errorMessage: t('calldelta.error.headerNotFound'),
                    });
                    return;
                }

                const parsedRows = getRowsFromXLSXData(data, headerLine);
                const forecast = parsedRows.filter(
                    (row) =>
                        Number.isFinite(row.Datum) &&
                        Number.isFinite(row.Intervall) &&
                        typeof row.fc_grp === 'string' &&
                        row.fc_grp.trim() !== ''
                );

                // Rows dropped by the filter above never reach the backend, so its
                // skippedRows can't include them — count them here for the warning.
                const clientSkippedRows = parsedRows.length - forecast.length;

                if (forecast.length === 0) {
                    setLoading(false);
                    closeDropzoneDialog();
                    showErrorUploadNotification({
                        dispatchAlert,
                        errorMessage: t('calldelta.error.noValidRows'),
                    });
                    return;
                }

                handleUploadForecast({ dataToUpload: forecast, monthForUploading, setPercentage })
                    .then((res) => {
                        const resData = parseUploadResponse(res, 'Calldelta');
                        const { success, errorMessage, notPresentWorktypes, skippedRows } = resData;

                        const totalSkippedRows = clientSkippedRows + (skippedRows || 0);

                        if (success) {
                            if (totalSkippedRows > 0) {
                                dispatchAlert({
                                    message: t('calldelta.skippedRows', { count: totalSkippedRows }),
                                    type: 'warning',
                                    useSnackbar: false,
                                });
                            }
                            if (notPresentWorktypes.length > 0) {
                                setNotPresentWorktypes(uniq(resData.notPresentWorktypes));
                                handleWorktypeListDialogOpen();
                            } else if (!totalSkippedRows) {
                                showSuccessfulUploadNotification(dispatchAlert);
                            }

                            socket.emit(SocketEvents.uploadedForecastDelta, { monthForUploading });
                        } else {
                            showErrorUploadNotification({
                                dispatchAlert,
                                errorMessage,
                            });
                        }
                    })
                    .catch((error) => {
                        Sentry.captureException(error);
                        showErrorUploadNotification({
                            dispatchAlert,
                            errorMessage: error.message,
                        });
                    })
                    .finally(async () => {
                        setCommonStateAfterDataUpload(setLoading, setPercentage, closeDropzoneDialog);
                        queryClient.invalidateQueries([
                            QueryKey.LastUploadDate + '-forecastDelta',
                            { worktype: selectedWorktype },
                        ]);
                    });
            };
            reader.readAsArrayBuffer(file);
        },
        [
            dispatchAlert,
            handleUploadForecast,
            handleWorktypeListDialogOpen,
            monthForUploading,
            selectedWorktype,
            socket,
            t,
        ]
    );

    const isLoading = useMemo(() => {
        return isSlotsLoading || isBookingsLoading;
    }, [isSlotsLoading, isBookingsLoading]);

    useEffect(() => {
        const onCreatedBookingDelta = ({ monthForCreatedBookings, worktypeForCreatedBookings }) => {
            const uploadedDate = dayjs().month(monthForCreatedBookings - 1);
            setSelectedDate(uploadedDate);
            setSelectedWorktype(worktypeForCreatedBookings);
        };
        socket.on(SocketEvents.createdBookingDelta, onCreatedBookingDelta);

        return () => {
            socket.off(SocketEvents.createdBookingDelta, onCreatedBookingDelta);
        };
    }, [isMounted, setSelectedWorktype, socket]);

    return (
        <Container>
            <GridContainer sx={loading ? classes.containerOpacity : {}}>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <CustomDatePicker
                        date={selectedDate}
                        views={['year', 'month']}
                        openTo="month"
                        onChange={(newValue) => handleDatePickerMonthChange(selectedDate, newValue, setSelectedDate)}
                    ></CustomDatePicker>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                    <WorktypeSelect
                        selectedWorktype={selectedWorktype}
                        worktypeOptions={worktypeOptions}
                        onChange={setSelectedWorktype}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={4}>
                    <LastUpload showInfoIcon={false} date={lastUploadDate}></LastUpload>
                </Grid>
                {canWrite && (
                    <Grid item xs={12} sm={6} md={3} lg={3} sx={classes.btnRightAligned}>
                        <UploadXlsxFileButton
                            buttonName={t('action.upload')}
                            monthForUploading={monthForUploading}
                            onChangeMonthForUploading={setMonthForUploading}
                            uploadFiles={uploadFiles}
                            dialogTitle={t('calldelta.uploadDialogTitle')}
                        ></UploadXlsxFileButton>
                    </Grid>
                )}
                <TableGridItem
                    ref={gridRef}
                    rowData={tableRows}
                    defaultColDef={{
                        editable: false,
                        filter: true,
                        sortable: true,
                    }}
                    columnDefs={columnDefs}
                    loading={isLoading}
                ></TableGridItem>
            </GridContainer>
            <PercentageLoadingOverlay loading={loading} percentage={percentage}></PercentageLoadingOverlay>
            <WorktypeListDialog
                open={openWorktypeListDialog}
                onClose={handleWorktypeListDialogClose}
                worktypes={notPresentWorktypes}
            ></WorktypeListDialog>
        </Container>
    );
};

export default CalldeltaOverview;
